import { CurrencyInput } from '@/components/ui/CurrencyInput'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import type { FieldInputProps } from './fieldInputProps'
import { amountsTotal, type AmountsValue, type Answers, formatDollars, pickedOptions } from './screenForm'

interface AmountsFieldProps extends FieldInputProps<AmountsValue> {
  /** The answers in this field's scope, where its sibling MULTI's picks are. */
  scope: Answers
}

/** A dollar amount for each option picked in the sibling MULTI, with a running total. */
export function AmountsField({ field, value, onChange, id, invalid, disabled, scope }: AmountsFieldProps) {
  const picked = pickedOptions(field, scope)
  if (!picked.length)
    return <p className="text-muted-foreground text-sm">Pick the types of work above first.</p>

  const labels = new Map(field.options.map((option) => [option.value, option.label]))
  const total = amountsTotal(field, value, scope)
  const target = field.totalAmount
  const offTarget = typeof target === 'number' && total !== target

  return (
    <div className="flex flex-col gap-3">
      {picked.map((option, index) => {
        const inputId = index === 0 ? id : `${id}-${option}`
        return (
          <div key={option} className="flex flex-col gap-1.5">
            <Label htmlFor={inputId} className="font-normal">
              {labels.get(option) ?? option}
            </Label>
            <CurrencyInput
              id={inputId}
              value={value[option] ?? null}
              onChange={(amount) => onChange({ ...value, [option]: amount })}
              disabled={disabled}
              aria-invalid={(invalid && typeof value[option] !== 'number') || undefined}
            />
          </div>
        )
      })}
      <p className={cn('text-sm font-medium', offTarget ? 'text-(--amber-text)' : 'text-muted-foreground')}>
        Total: {formatDollars(total)}
        {typeof target === 'number' && ` of ${formatDollars(target)} ${field.totalLabel}`.trimEnd()}
      </p>
    </div>
  )
}
